<template>
  <b-row class="h-100">
    <b-col class="container">
      <div
        class="d-flex flex-row justify-content-between align-items-center pl-4 pr-4 pt-3"
      >
        <h5>
          Treatment Plans<br />
          <span class="grid-count color-custom-dark-grey"
            >({{ list.length }} documents)</span
          >
        </h5>
        <div>
          <b-button
            v-if="formHasComponents && masterTreatmentPlanForms.length"
            variant="primary"
            class="pl-3 pr-3 pt-1 pb-1 mr-2"
            size="sm"
            @click="openTreatmentPlanModal"
          >
            <i class="fa fa-plus mr-2"></i> New
          </b-button>
          <b-button
            v-if="!!formId"
            variant="primary"
            class="pl-3 pr-3 pt-1 pb-1 mr-2"
            size="sm"
            @click="addSubmitFile"
          >
            <i class="fa fa-cloud-upload-alt mr-2"></i> Upload
          </b-button>
        </div>
      </div>
      <div class="mt-3 mb-0 px-4 w-100 d-flex flex-row justify-content-between">
        <div></div>
        <div class="d-flex flex-row justify-content-end align-items-center">
          <top-paginator
            :total="itemsFilter.length"
            :page="paginator.page"
            :perPage="paginator.perPage"
            @change-page="changePage"
          />
        </div>
      </div>
      <div class="px-4">
        <grid
          v-if="client && eoc"
          :groupFeature="false"
          :store="gridConfig.store"
          :columns="columns"
          :autoHeight="true"
          :data="items"
          :selectionMode="gridConfig.selectionMode"
          :columnLines="gridConfig.columnLines"
          :searchFeature="true"
          :stripeFeature="false"
          :excelExporterFeature="excelExporterFeature"
          @storesort="listeners.storeSort"
          @renderrows="hookDropdownButtons"
          @renderrow="hookDropdownButtons"
        />
      </div>

      <b-modal
        centered
        ref="treatment-plan-selector-modal"
        title="Select treatment plan form"
        header-bg-variant="primary"
        header-text-variant="white"
        @ok="addSubmit"
        no-close-on-backdrop
        header-class="bg-purple"
      >
        <template v-slot:modal-footer="{ ok, cancel }">
          <b-button variant="secondary" @click="cancel()">
            Cancel
          </b-button>
          <b-button variant="primary" @click="ok()">
            Continue
          </b-button>
        </template>
        <b-form-group>
          <b-form-radio-group
            name="selected-treatment-plan"
            v-model="selectedTreatmentPlan"
            :options="masterTreatmentPlanForms"
            stacked
          ></b-form-radio-group>
        </b-form-group>
      </b-modal>

      <document-file-add-edit-modal
        v-if="showAddFileEditor"
        v-model="showAddFileEditor"
        :formId="formId"
        :eocId="eocId"
        @onModify="onModifyAddFile"
        :clientId="clientId"
        :submitId.sync="submitId"
      />
      <document-add-edit-modal
        v-if="showAddDocumentEditor"
        v-model="showAddDocumentEditor"
        :formId="submitFormId"
        @onModify="onModifyAddDocument"
        :clientId="clientId"
        :eocId="eocId"
        :submitId="submitId"
        :parentDocumentId="parentDocumentId"
        :otherResults="otherResults"
        :defaultValues="defaultValues"
      />
      <document-share-multiple-modal
        v-if="showSendLinkEditor"
        v-model="showSendLinkEditor"
        @onModify="onModifySendLink"
        :patientId="clientId"
        :forms="selectedForms"
        :eocId="eocId"
      />
      <document-view-modal
        v-if="showDocumentViewer"
        v-model="showDocumentViewer"
        :documentId="submitId"
        @onModify="onModifyAddDocument"
      />
    </b-col>
  </b-row>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import zipcelx from "zipcelx";
import Grid from "../../third_party/grid/Grid.vue";
import eocServer from "../../server/eoc";
import patientServer from "../../server/patient";
import formSubmitterServer from "../../server/formSubmitter";
import {
  getElementComponentData,
  getElementFromId,
  getFormBuilderComponents
} from "../../../../Breeze.FormBuilder.Frontend/helpers/formBuilder";
import { formatDate, formatDateTimeMysql } from "../../helpers/date";
import DocumentFileAddEditModal from "./document/DocumentFileAddEditModal.vue";
import DocumentAddEditModal from "./document/DocumentAddEditModal.vue";
import DocumentShareMultipleModal from "./document/DocumentShareMultipleModal.vue";
import DocumentViewModal from "./document/DocumentViewModal.vue";
import TopPaginator from "../controls/TopPaginator.vue";

export default {
  name: "EocViewTreatmentPlanDocuments",
  components: {
    Grid,
    TopPaginator,
    DocumentShareMultipleModal,
    DocumentFileAddEditModal,
    DocumentAddEditModal,
    DocumentViewModal
  },
  props: ["clientId", "eocId", "showing"],
  data() {
    const self = this;
    return {
      list: [],
      gridConfig: {
        minHeight: "20em",
        selectionMode: {
          row: true
        },
        columnLines: false,
        store: {
          sorters: []
        }
      },
      paginator: {
        page: 0,
        perPage: 10
      },
      search: "",
      form: null,
      showAddFileEditor: false,
      showAddDocumentEditor: false,
      showSendLinkEditor: false,
      showDocumentViewer: false,
      selectedForms: [],
      submitFormId: null,
      submitId: null,
      parentDocumentId: null,
      otherResults: [],
      defaultValues: null,
      client: null,
      eoc: null,
      filter: {
        orderBy: "",
        orderByDescending: false
      },
      selectedTreatmentPlan: null,
      listeners: {
        storeSort: sortData => {
          if (sortData.sorters.length == 1) {
            const sorter = sortData.sorters[0];
            const ascending = sorter.ascending;
            const field = sorter.field;
            self.filter.orderBy = field;
            self.filter.orderByDescending = !ascending;
            self.changePage(0);
          }
        }
      }
    };
  },
  async mounted() {
    await this.fetchData();
    this.itemsFilter();
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess"]),
    ...mapActions("formSubmitter", ["deleteFormSubmitterSubmit"]),
    ...mapActions("formBuilder", ["fetchFormBuilderForms"]),
    async fetchData() {
      this.getEoc();
      this.getClient();
      await this.fetchFormBuilderForms({
        category: "masterTreatmentPlan"
      });
      await this.fetchFormSubmitterSubmits();
      if (this.formId) {
        this.form = await this.getFormBuilderFormById(this.formId);
      }
    },
    async getEoc() {
      this.eoc = await eocServer.get(this.eocId);
    },
    async getClient() {
      this.client = await patientServer.get(this.clientId);
    },
    async fetchFormSubmitterSubmits() {
      const submits = await formSubmitterServer.queryByCategInTreatmentPlan(
        "masterTreatmentPlan",
        this.clientId,
        this.eocId,
        this.filter
      );
      this.list = submits;
    },
    addSubmit() {
      this.submitId = null;
      this.submitFormId = this.selectedTreatmentPlan;
      this.parentDocumentId = null;
      this.otherResults = [];
      this.defaultValues = null;
      this.showAddDocumentEditor = true;
    },
    addSubmitFile() {
      this.submitId = null;
      this.showAddFileEditor = true;
    },
    changePage(page) {
      this.paginator.page = page;
    },
    onModifySendLink() {
      this.showSendLinkEditor = false;
    },
    onModifyAddFile() {
      this.showAddFileEditor = false;
      this.fetchFormSubmitterSubmits();
    },
    onModifyAddDocument() {
      this.showAddDocumentEditor = false;
      this.fetchFormSubmitterSubmits();
    },
    exportExcel() {
      this.$refs.grid.exportExcel();
    },
    openTreatmentPlanModal() {
      if (this.masterTreatmentPlanForms.length > 1) {
        this.openTreatmentPlanSelectorModal();
      } else {
        this.selectedTreatmentPlan = this.formId;
        this.addSubmit();
      }
    },
    openTreatmentPlanSelectorModal() {
      this.selectedTreatmentPlan = this.formId;
      this.$refs["treatment-plan-selector-modal"].show();
    },
    buildListOfTPAndReviews(newList, newListReview) {
      let finalList = [];
      for (let index = 0; index < newList.length; index++) {
        const item = newList[index];
        finalList.push(item);
        const itemReviewList = newListReview.filter(
          itemReview => itemReview.parentDocumentId == item.id
        );
        finalList = [
          ...finalList,
          ...itemReviewList.map((itemReview, itemReviewIndex) => ({
            ...itemReview,
            treatmenPlanIndex: itemReviewIndex
          }))
        ];
      }
      return finalList;
    }
  },
  computed: {
    ...mapState("formBuilder", {
      forms: state => state.list
    }),
    ...mapGetters("layout", ["hasCurrentUserDocumentPermission"]),
    ...mapGetters("formSubmitter", ["superComputeFormSubmitterValue"]),
    ...mapGetters("client", ["selectedLocation"]),
    formId() {
      if (
        this.selectedLocation?.treatmentPlanFormId &&
        !!this.masterTreatmentPlanForms.find(
          f => f.value == this.selectedLocation?.treatmentPlanFormId
        )
      ) {
        return this.selectedLocation?.treatmentPlanFormId;
      } else {
        return this.masterTreatmentPlanForms[0]?.value;
      }
    },
    itemsFilter() {
      let newList = this.list.filter(item => !item.parentDocumentId);
      let newListReview = this.list.filter(item => item.parentDocumentId);
      let finalList = this.buildListOfTPAndReviews(newList, newListReview);
      if (this.filter.orderBy == "documentRefNumber") {
        finalList.sort((a, b) => {
          return a.documentRefNumber
            .toString()
            .localeCompare(b.documentRefNumber.toString());
        });
        if (this.filter.orderByDescending) {
          finalList.reverse();
        }
      }
      if (this.filter.orderBy == "status") {
        finalList.sort((a, b) => {
          return a.status.localeCompare(b.status);
        });
        if (this.filter.orderByDescending) {
          finalList.reverse();
        }
      }
      if (this.filter.orderBy == "effectiveDate") {
        newList.sort((a, b) => {
          return formatDateTimeMysql(a.effectiveDate).localeCompare(
            formatDateTimeMysql(b.effectiveDate)
          );
        });
        newListReview.sort((a, b) => {
          return formatDateTimeMysql(a.effectiveDate).localeCompare(
            formatDateTimeMysql(b.effectiveDate)
          );
        });
        if (this.filter.orderByDescending) {
          newList.reverse();
          newListReview.reverse();
        }
        finalList = this.buildListOfTPAndReviews(newList, newListReview);
      }
      if (this.filter.orderBy == "createdAt") {
        finalList.sort((a, b) => {
          return formatDateTimeMysql(a.createdAt).localeCompare(
            formatDateTimeMysql(b.createdAt)
          );
        });
        if (this.filter.orderByDescending) {
          finalList.reverse();
        }
      }
      if (this.filter.orderBy == "user") {
        finalList.sort((a, b) => {
          let compareA = a.createdBy
            ? (a.createdBy.lastName + a.createdBy.firstName).toLowerCase()
            : "-";
          let compareB = b.createdBy
            ? (b.createdBy.lastName + b.createdBy.firstName).toLowerCase()
            : "-";
          return compareA.localeCompare(compareB);
        });
        if (this.filter.orderByDescending) {
          finalList.reverse();
        }
      }
      if (this.filter.orderBy == "fileName") {
        finalList.sort((a, b) => {
          let compareA = a.fileName ? a.fileName.toLowerCase() : "-";
          let compareB = b.fileName ? b.fileName.toLowerCase() : "-";
          return compareA.localeCompare(compareB);
        });
        if (this.filter.orderByDescending) {
          finalList.reverse();
        }
      }
      if (this.filter.orderBy == "mode") {
        finalList.sort((a, b) => {
          let compareA = a.mode == "File" ? 2 : 1;
          let compareB = b.mode == "File" ? 2 : 1;
          return compareA.toString().localeCompare(compareB.toString());
        });
        if (this.filter.orderByDescending) {
          finalList.reverse();
        }
      }
      return finalList;
    },
    items() {
      return this.itemsFilter.slice(
        this.paginator.page * this.paginator.perPage,
        (this.paginator.page + 1) * this.paginator.perPage
      );
    },
    ...mapGetters("formBuilder", ["getFormBuilderFormById"]),
    hasSubmits() {
      return this.items.filter(item => item.mode == "Results").length > 0;
    },
    columns() {
      let columns = [];
      columns.push({
        text: "REF#",
        field: "documentRefNumber",
        type: "template",
        width: 90,
        editor: false,
        template: data => data.record.data.documentRefNumber,
        sortable() {
          return false;
        }
      });
      columns.push({
        text: "Type",
        field: "parentDocumentId",
        type: "template",
        width: 230,
        editor: false,
        sortable: false,
        template: data => {
          return `<span class="d-flex align-items-center ${
            data.record.data.parentDocumentId ? "ml-4" : ""
          }">${
            data.record.data.parentDocumentId
              ? "<i style='font-size:6px' class='fa fa-circle mr-2'></i>"
              : ""
          }<span>${
            data.record.data.parentDocumentId
              ? "Treatment Plan Review " +
                (data.record.data.treatmenPlanIndex + 1)
              : "Treatment Plan"
          }</span></span>`;
        }
      });
      columns.push({
        text: "Status",
        field: "status",
        type: "template",
        width: 130,
        editor: false,

        template: data => {
          const status = data.record.data.status;
          let className = "warning";
          if (status == "Completed") {
            className = "success";
          }
          if (status == "Draft") {
            className = "danger";
          }
          return `<span class="badge-v2 py-2 badge-${className} badge-fixed-size">${status}</span>`;
        },
        sortable() {
          return false;
        }
      });
      columns.push({
        text: "Effective date",
        field: "effectiveDate",
        type: "template",
        width: 180,
        editor: false,
        template: data =>
          data.record.data.effectiveDate
            ? formatDate(data.record.data.effectiveDate)
            : "",
        sortable() {
          return false;
        }
      });
      columns.push({
        text: "Created on",
        field: "createdAt",
        type: "template",
        width: 180,
        editor: false,
        template: data => `${formatDate(data.record.data.createdAt)}`,
        sortable() {
          return false;
        }
      });
      columns.push({
        text: "Created by",
        field: "user",
        type: "template",
        width: 260,
        editor: false,
        template: data =>
          `${
            data.record.data.createdBy
              ? this.formatUser(data.record.data.createdBy)
              : "-"
          }`,
        sortable() {
          return false;
        }
      });
      columns.push({
        text: "File Name",
        field: "fileName",
        type: "template",
        width: 260,
        editor: false,
        template: data =>
          `${data.record.data.fileName ? data.record.data.fileName : "-"}`,
        sortable() {
          return false;
        }
      });
      columns.push({
        text: "Type",
        field: "mode",
        type: "template",
        width: 90,
        editor: false,
        template: ({ record }) =>
          record.data.mode === "File" ? "File" : "Document",
        sortable() {
          return false;
        }
      });

      columns.push({
        text: "Tools",
        type: "action",
        cellCls: "pointer-cell",
        field: "action",
        align: "right",
        width: 80,
        actions: [
          {
            tooltip: "View",
            renderer: ({ record }) =>
              this.hasCurrentUserDocumentPermission(
                record.data.formId,
                "isReadOnly"
              )
                ? `<i class="fa fa-search-plus color-custom-dark-grey hover-text-primary b-action-item grid-action-icon"></i>`
                : ``,
            onClick: ({ record }) => {
              if (record.data.mode == "File") {
                const url = formSubmitterServer.viewUrl(record.data.id);
                window.open(url, "_blank");
              } else {
                this.submitId = record.data.id;
                this.showDocumentViewer = true;
              }
            }
          },
          {
            tooltip: "More",
            renderer: ({ record }) => {
              if (
                !this.hasCurrentUserDocumentPermission(
                  record.data.formId,
                  "isReadOnly"
                )
              )
                return "";
              const actions = [];

              if (
                record.data.mode != "File" &&
                this.hasCurrentUserDocumentPermission(
                  record.data.formId,
                  "hasEntryAccess"
                )
              ) {
                actions.push({
                  content: "New Review",
                  callback: async () => {
                    this.submitId = null;
                    this.submitFormId = record.data.formId;
                    this.parentDocumentId = record.data.parentDocumentId
                      ? record.data.parentDocumentId
                      : record.id;

                    this.otherResults = [
                      { ...record.data },
                      ...this.list.filter(
                        mTPR => mTPR.parentDocumentId == record.id
                      )
                    ];

                    this.form = await this.getFormBuilderFormById(this.submitFormId);
                    
                    const mapAutocompleteFunctionResult = this.superComputeFormSubmitterValue(
                      {
                        elementId: this.form.current.content.id,
                        property: "mapAutocompleteFunctionValue",
                        value: `{{
                      var lastDocument = getLastOtherResult();
                      var initialValues = lastDocument.results;
                      ${this.form.current.content.properties.mapAutocompleteFunctionBody}
                      initialValues;
                      }}`,
                        defaultValue: {},
                        statements: true,
                        form: {
                          ...this.form,
                          otherResults: [
                            { ...record.data },
                            ...this.list.filter(
                              mTPR => mTPR.parentDocumentId == record.id
                            )
                          ]
                        }
                      }
                    );
                    for (var key in mapAutocompleteFunctionResult) {
                      if (key.includes("Signature")) {
                        delete mapAutocompleteFunctionResult[key];
                      }
                    }
                    this.defaultValues = mapAutocompleteFunctionResult;

                    this.showAddDocumentEditor = true;
                  }
                });
              }

              if (
                record.mode != "File" &&
                (record.status == "Draft" ||
                  this.hasCurrentUserDocumentPermission(
                    record.data.formId,
                    "isReadOnly"
                  ))
              ) {
                actions.push({
                  content: "Send Form",
                  callback: () => {
                    this.selectedForms = [
                      {
                        formId: this.form.id,
                        formVersionId: record.versionId,
                        formGroupId: null,
                        documentId: record.id,
                        documentGroupId: null,
                        patientId: this.clientId,
                        episodeOfCareId: this.eocId,
                        status: record.status
                      }
                    ];
                    this.showSendLinkEditor = true;
                  }
                });
              }

              if (
                (record.status == "Completed" &&
                  this.hasCurrentUserDocumentPermission(
                    record.form.id,
                    "hasCompleteAccess"
                  )) ||
                (record.status != "Completed" &&
                  this.hasCurrentUserDocumentPermission(
                    record.form.id,
                    "hasEntryAccess"
                  ))
              ) {
                actions.push({
                  content: "Edit",
                  callback: () => {
                    this.submitId = record.id;
                    this.submitFormId = record.data.formId;
                    this.parentDocumentId = record.data.parentDocumentId;
                    if (record.mode == "File") this.showAddFileEditor = true;
                    else {
                      this.showAddDocumentEditor = true;
                      if (record.data.parentDocumentId) {
                        this.otherResults = [
                          ...this.list.filter(
                            mTPR =>
                              mTPR.parentDocumentId ==
                                record.data.parentDocumentId &&
                              mTPR.id != record.id
                          )
                        ];
                      } else this.otherResults = [];
                    }
                  }
                });
              }

              if (
                record.status != "Completed" &&
                this.hasCurrentUserDocumentPermission(
                  record.data.formId,
                  "hasEntryAccess"
                )
              ) {
                actions.push({
                  content: "Delete",
                  callback: () => {
                    this.$dialog
                      .confirm(
                        {
                          title: record.data.parentDocumentId
                            ? "Are you sure you want to delete this record?"
                            : "Are you sure you want to delete this Treatment plan and ALL its reviews?"
                        },
                        {
                          view: "confirm-delete"
                        }
                      )
                      .then(async () => {
                        const result = await this.deleteFormSubmitterSubmit({
                          submitId: record.id,
                          formId: record.data.formId,
                          clientId: this.clientId,
                          eocId: this.eocId
                        });
                        if (result.errorCode) {
                          this.$dialog.alert("Error: " + result.message);
                        } else {
                          this.showAlertSuccess();
                          this.fetchFormSubmitterSubmits();
                        }
                      })
                      .catch(function() {});
                  }
                });
              }

              return this.createDropdownButton({
                items: actions
              });
            }
          }
        ]
      });
      return columns;
    },
    formHasComponents() {
      return (
        this.form &&
        getFormBuilderComponents(this.form.current.content).length > 0
      );
    },
    excelExporterFeature() {
      let columns = [
        {
          text: "Created on",
          field: "createdAt",
          width: 400,
          renderer(data) {
            return `${formatDate(data.record.data.createdAt)} by ${
              data.record.data.createdBy ? data.record.data.createdBy.name : "-"
            }`;
          }
        },
        {
          text: "Status",
          field: "status",
          width: 50,
          renderer({ record }) {
            return record.status;
          }
        },
        {
          text: "Effective Date",
          field: "effectiveDate",
          width: 50,
          renderer(data) {
            return data.record.data.effectiveDate
              ? formatDate(data.record.data.effectiveDate)
              : "";
          }
        }
      ];

      if (this.form && this.hasSubmits) {
        this.form.current.content.grid.components.forEach(component => {
          const jsonElement = getElementFromId(
            this.form.current.content,
            component
          );
          if (jsonElement) {
            const componentData = getElementComponentData(
              jsonElement.elType,
              jsonElement.version
            );
            columns.push({
              text: jsonElement.properties.label
                ? jsonElement.properties.label
                : "",
              field: `results.${component}`,
              width: 50,
              ...componentData.gridAttrs
            });
          }
        });
      }

      return {
        zipcelx,
        filename: `${this.eoc.mrn}_${this.client.firstName}_${this.client.middleName}_${this.client.lastName}_Document_${this.form?.name}`,
        exporterConfig: {
          columns
        }
      };
    },
    masterTreatmentPlanForms() {
      return this.forms["masterTreatmentPlan"]
        .filter(form =>
          this.hasCurrentUserDocumentPermission(form.id, "hasEntryAccess")
        )
        .map(form => ({
          value: form.id,
          text: form.name
        }));
    }
  },
  watch: {
    showing: async function(newValue) {
      if (newValue) {
        await this.fetchData();
      }
    }
  }
};
</script>

<style scoped>
.grid-count {
  font-size: 0.8rem;
}
</style>
